import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

export function AuthCallback() {
  const [message, setMessage] = useState('Authenticating...');
  const [isLoading, setIsLoading] = useState(true); // 1. Add loading state

  useEffect(() => {
    const exchangeCodeForToken = async () => {
      // If we are not in the initial loading state, do nothing.
      // This prevents the effect from running again on re-renders.
      if (!isLoading) return;

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');

      if (error) {
        const errorDescription = params.get('error_description');
        console.error('OAuth Error:', error, errorDescription);
        setMessage(`Authentication failed: ${errorDescription || 'An error occurred during authentication.'}`);
        setIsLoading(false);
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
        return;
      }

      if (!code) {
        setMessage('Authentication failed: No authorization code found.');
        setIsLoading(false);
        return;
      }

      try {
        // Determine the OAuth provider from the current path
        // The path will be like /api/auth/callback/github or /api/auth/callback/google
        const pathParts = window.location.pathname.split('/');
        const provider = pathParts[pathParts.length - 1]; // Gets 'github', 'google', etc.
        
        // Manually send the OAuth callback data to the backend
        // by reconstructing the proper callback URL
        const callbackUrl = `/api/auth/callback/${provider}?${window.location.search.substring(1)}`;
        
        // Make a GET request to trigger the backend callback processing
        const response = await fetch(callbackUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Auth callback error:', errorData);
          
          // Provide more specific error messages based on the error type
          if (errorData.isInvalidGrant) {
            setMessage('Authorization error: The login session may have expired. Please try logging in again.');
          } else if (errorData.details && errorData.details.error_description) {
            setMessage(`Authentication failed: ${errorData.details.error_description}`);
          } else {
            setMessage(`Authentication failed: ${errorData.error || 'Unknown error'}`);
          }
          
          setIsLoading(false);
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
          return;
        }

        // If successful, redirect to console
        // The session should now be established
        setMessage('Authentication successful! Redirecting...');
        setTimeout(() => {
          window.location.href = '/console';
        }, 500);

      } catch (error) {
        setMessage('Authentication failed. Please try again.');
        setIsLoading(false);
        console.error('Network error during auth callback:', error);
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    };

    exchangeCodeForToken();
  }, [isLoading]); // Depend on the loading state

  return (
    <div className="flex min-h-svh w-full items-center justify-center">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>{message}</span>
      </div>
    </div>
  );
}